/* Radon map — zoom/pan overlay (RadonTest.ca layer, NOT part of Ger's v9 engine).
 *
 * Adds Google-Maps-style zoom on top of the engine's own navigation without
 * touching engine code. Model: the ENGINE owns a baseline view (`engine`) —
 * national, a province, or a search zoomToPoint; the overlay owns the
 * DISPLAYED view (`view`), which users move with buttons / ctrl+wheel /
 * double-click / drag. Effective user zoom z = engine.w / view.w.
 *
 * Engine writes are detected via a viewBox MutationObserver (own writes are
 * recognized by exact-string compare — a sync flag can't work, observer
 * callbacks are async). Two cases:
 *   · engine write right after a click INSIDE the svg while the user has a
 *     modified view (zoomed/panned): treat as a selection side-effect — adopt
 *     the engine's target as the new BASELINE but keep the user's displayed
 *     view (written back within the observer microtask = zero visual jump).
 *     So clicking a region/dot while zoomed no longer resets the view.
 *   · any other engine write (province click from rest, search, crumb):
 *     adopt it as both baseline and view — normal navigation.
 *
 * Zoom/reset are ANIMATED (rAF tween, ~220ms ease-out, glyphs counter-scaled
 * per frame). Pan works whenever the view is tighter than national — including
 *  the engine's own province views — and the view rectangle is clamped inside
 * the national box (0 0 1000 900) so the map can never be dragged out of
 * sight. Zoom-out is allowed back up to the national frame even from a
 * province baseline.
 *
 * Dots/labels/strokes are counter-scaled (÷z) so they keep apparent size;
 * originals cached in data-rt-* attributes; engine re-renders replace nodes,
 * so a childList observer re-applies scaling to fresh nodes.
 *
 * Caps: zoom-in to min(engine view ÷ MAX_FACTOR, MIN_W units wide); zoom-out
 * to the national frame. Pure viewBox math — vector only, no raster cost.
 *
 * Known nuances (docs/RADONMAP.md §7): the engine's share/compare canvas
 * snapshots the CURRENT view; pinch-zoom not implemented (buttons work).
 */
(function () {
  "use strict";

  // ── Restore the engine's init chain (Tu r14) ──────────────────────────────
  // The engine boots via  setTimeout(function(){ try{ …;buildSignup();
  // buildShare();initGuidePopup();paintDir(); }catch(e){} }).  buildSignup()
  // does  $("#suform").addEventListener("submit", …)  with NO null guard, and
  // our install has no signup form (the guide/signup feature was deferred) —
  // so it throws, the swallowing catch eats the error, and EVERYTHING after it
  // (buildShare → the "Use or share this map" copy-link / copy-embed / download
  // buttons; initGuidePopup; the initial paintDir) silently never runs. That's
  // why those three buttons are dead.
  //
  // Fix at the source instead of re-wiring each button: drop a hidden #suform
  // stub into the DOM so buildSignup() wires it harmlessly and the chain
  // completes, restoring the engine's OWN handlers for all of it. This runs
  // synchronously — the overlay is a <head> defer script (executes before the
  // engine's body defer script) and the engine's init is inside setTimeout — so
  // the stub is present well before buildSignup() runs. (Flagged to Ger: add the
  // null guard + restore the real form; see docs/RADONMAP.md §12.)
  (function ensureSuform() {
    if (document.getElementById("suform")) return;
    var host = document.querySelector(".rtm") || document.body || document.documentElement;
    if (!host) return;
    var f = document.createElement("form");
    f.id = "suform";
    f.setAttribute("aria-hidden", "true");
    f.style.display = "none";
    var i = document.createElement("input");
    i.id = "suemail";
    i.type = "email";
    f.appendChild(i);
    host.appendChild(f);
  })();

  var TRIES = 0;
  function boot() {
    var svg = document.getElementById("map");
    var wrap = svg && svg.closest(".mapwrap");
    if (!svg || !wrap) {
      if (TRIES++ < 20) setTimeout(boot, 250);
      return;
    }
    if (svg.__rtZoomWired) return;
    svg.__rtZoomWired = true;

    var FR = (document.documentElement.lang || "").toLowerCase().indexOf("fr") === 0;
    var NATION = { x: 0, y: 0, w: 1000, h: 900 };
    var MIN_W = 40; // absolute deepest view (viewBox units)
    var MAX_FACTOR = 8; // deepest relative to the engine's baseline
    var CLICK_GRACE_MS = 600;

    function readVB() {
      var v = (svg.getAttribute("viewBox") || "0 0 1000 900").trim().split(/[\s,]+/).map(Number);
      return { x: v[0], y: v[1], w: v[2], h: v[3] };
    }
    function vbStr(v) {
      return v.x + " " + v.y + " " + v.w + " " + v.h;
    }

    var engine = readVB(); // engine-owned baseline
    var view = readVB(); // displayed view (overlay-owned)

    // Drill-state flag for CSS: the .provb grey boundary layer has its OWN
    // geometry (a 13-path detailed coastline set) that can never align with
    // the CD tile edges — at drilled views it floats offshore of the tiles
    // (Tu screenshot, 2026-07-14). radontest-custom.css hides .provb when
    // this flag is on; at national the layer stays (its mismatch is invisible
    // at that scale and it gives the map its coastline detail).
    function syncDrillFlag() {
      svg.setAttribute("data-rt-drill", engine.w < 950 ? "1" : "");
    }
    syncDrillFlag();
    var lastWrite = null;
    var lastClickTs = 0;
    var anim = null;

    function z() {
      return engine.w / view.w;
    }
    function userModified() {
      return (
        Math.abs(view.x - engine.x) > 0.5 ||
        Math.abs(view.y - engine.y) > 0.5 ||
        Math.abs(view.w - engine.w) > 0.5
      );
    }
    function minW() {
      return Math.max(MIN_W, engine.w / MAX_FACTOR);
    }
    function clampView(v) {
      v.w = Math.min(Math.max(v.w, minW()), NATION.w);
      v.h = v.w * (NATION.h / NATION.w);
      v.x = Math.min(Math.max(v.x, NATION.x), NATION.x + NATION.w - v.w);
      v.y = Math.min(Math.max(v.y, NATION.y), NATION.y + NATION.h - v.h);
      return v;
    }
    function setView(v, skipGlyphs) {
      view = v;
      lastWrite = vbStr(v);
      svg.setAttribute("viewBox", lastWrite);
      if (!skipGlyphs) scaleGlyphs();
      syncButtons();
    }

    // ── animated transition (ease-out cubic, ~220ms) ──
    function animateTo(target) {
      target = clampView(target);
      if (anim) cancelAnimationFrame(anim.raf);
      var from = { x: view.x, y: view.y, w: view.w, h: view.h };
      var t0 = performance.now();
      var DUR = 220;
      function step(now) {
        var p = Math.min((now - t0) / DUR, 1);
        var e = 1 - Math.pow(1 - p, 3);
        setView(
          {
            x: from.x + (target.x - from.x) * e,
            y: from.y + (target.y - from.y) * e,
            w: from.w + (target.w - from.w) * e,
            h: from.h + (target.h - from.h) * e,
          },
          false
        );
        if (p < 1) {
          anim.raf = requestAnimationFrame(step);
        } else {
          anim = null;
        }
      }
      anim = { raf: requestAnimationFrame(step) };
    }

    // ── glyph scaling ──
    // Bare city dots (circle.dot) keep constant apparent size while zoomed
    // (r ÷ z). LABEL ASSEMBLIES are scaled AS UNITS: the engine builds each
    // one as an anchored group — g.pcity (ring + dot + name + ▾ caret,
    // origin = the city point) and g.natcity (pulse + dot + name) — plus the
    // #searchpin group (absolute coords around the pin). Scaling only the
    // text (earlier approach) tore the assemblies apart when zoomed: the
    // pill halo, caret and y-offsets kept their map-unit size/positions
    // (Tu screenshot, 2026-07-14). A group transform keeps every part
    // pixel-aligned at any zoom. The group scale also applies the apparent
    // CAP (engine draws province labels ~36–42 screen px — too dominant) and
    // a gentle z^-0.4 shrink while zooming so lettering recedes.
    var LABEL_CAP_PX = 24; // max apparent label font size at any view
    var LABEL_MIN_PX = 9; // readability floor
    function scaleGlyphs() {
      var k = 1 / z();
      var i, el;
      // bare dots only — pcring/pcdot/natpulse/natdot/pin circles are scaled
      // via their group transforms
      var circles = svg.querySelectorAll("circle.dot");
      for (i = 0; i < circles.length; i++) {
        el = circles[i];
        if (!el.getAttribute("data-rt-r0")) el.setAttribute("data-rt-r0", el.getAttribute("r") || "0");
        el.setAttribute("r", (parseFloat(el.getAttribute("data-rt-r0")) * k).toFixed(2));
        if (!el.getAttribute("data-rt-sw0")) {
          // CSS class sets .dot's stroke-width — inline style beats it
          el.setAttribute("data-rt-sw0", parseFloat(getComputedStyle(el).strokeWidth) || 1);
        }
        el.style.strokeWidth = (parseFloat(el.getAttribute("data-rt-sw0")) * k).toFixed(2);
      }

      // region/province border strokes stay hairline while zoomed (÷z) —
      // without this the 0.4-unit white borders fatten into slabs at deep
      // zoom. Includes .provb (the detailed grey province-boundary layer).
      // The fill-coloured underlay (bootBorders) deliberately keeps its UNIT
      // stroke: the geometry cracks it covers are in map units.
      var areas = svg.querySelectorAll("path.cd, path.hc, path.prov, path.provb");
      for (i = 0; i < areas.length; i++) {
        el = areas[i];
        if (!el.getAttribute("data-rt-psw")) {
          el.setAttribute("data-rt-psw", parseFloat(getComputedStyle(el).strokeWidth) || 0.4);
        }
        el.style.strokeWidth = (parseFloat(el.getAttribute("data-rt-psw")) * k).toFixed(3);
      }

      var rect = svg.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      var sNow = Math.min(rect.width / view.w, rect.height / view.h); // px per unit, current view
      var sEng = Math.min(rect.width / engine.w, rect.height / engine.h); // px per unit, engine view
      var zoomDamp = z() > 1 ? Math.max(0.5, Math.pow(z(), -0.4)) : 1;

      function groupScale(g) {
        var t = g.querySelector("text");
        if (!t) return null;
        if (!g.getAttribute("data-rt-f0")) {
          g.setAttribute("data-rt-f0", parseFloat(getComputedStyle(t).fontSize) || 12);
        }
        var f0 = parseFloat(g.getAttribute("data-rt-f0"));
        var apparent0 = f0 * sEng; // label size the engine intended, screen px
        var target = Math.max(LABEL_MIN_PX, Math.min(apparent0, LABEL_CAP_PX) * zoomDamp);
        return target / (f0 * sNow);
      }

      // anchored groups: origin is already the anchor — append scale()
      var groups = svg.querySelectorAll("g.pcity, g.natcity");
      for (i = 0; i < groups.length; i++) {
        el = groups[i];
        var s = groupScale(el);
        if (s == null) continue;
        if (!el.getAttribute("data-rt-t0")) el.setAttribute("data-rt-t0", el.getAttribute("transform") || "");
        el.setAttribute("transform", el.getAttribute("data-rt-t0") + " scale(" + s.toFixed(4) + ")");
      }

      // search pin: children use absolute coords — scale around the pin point
      var pin = svg.querySelector("#searchpin .pindot");
      if (pin) {
        var pg = pin.parentNode;
        var ax = parseFloat(pin.getAttribute("cx"));
        var ay = parseFloat(pin.getAttribute("cy"));
        var ps = groupScale(pg);
        if (ps != null && isFinite(ax) && isFinite(ay)) {
          pg.setAttribute(
            "transform",
            "translate(" + ax + "," + ay + ") scale(" + ps.toFixed(4) + ") translate(" + -ax + "," + -ay + ")"
          );
        }
      }
    }

    // ── engine navigation detection ──
    new MutationObserver(function () {
      var now = svg.getAttribute("viewBox");
      if (now === lastWrite) return; // our own write
      var attempted = readVB(); // the engine's intended view
      if (userModified() && Date.now() - lastClickTs < CLICK_GRACE_MS) {
        // Selection side-effect (user clicked a region/dot while zoomed/panned):
        // adopt the engine's target as the new BASELINE (so glyph sizing and
        // zoom caps stay coherent with what the engine rendered) but keep the
        // user's displayed view. Runs in the observer microtask → no flicker.
        engine = attempted;
        syncDrillFlag();
        setView(clampView(view));
        return;
      }
      if (anim) {
        cancelAnimationFrame(anim.raf);
        anim = null;
      }
      engine = attempted; // normal navigation: baseline = view = engine target
      view = attempted;
      lastWrite = null;
      syncDrillFlag();
      scaleGlyphs();
      syncButtons();
    }).observe(svg, { attributes: true, attributeFilter: ["viewBox"] });

    // engine re-renders replace dot/label nodes — re-apply scaling on every
    // render (the label apparent-size cap applies at plain engine views too)
    var raf = null;
    new MutationObserver(function () {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        scaleGlyphs();
      });
    }).observe(svg, { childList: true, subtree: true });

    // Single capture-phase click gate. Handles three things:
    //  1. remembers in-map clicks so the viewBox observer can tell selection
    //     side-effects from explicit navigation (search/crumb/table links);
    //  2. swallows the click that follows a real drag-pan;
    //  3. label hotspots: province city labels (text.pclbl — "Toronto",
    //     "Ottawa-Gatineau") have no engine handler and are click-transparent
    //     (CSS pointer-events:none), so a click on the lettering would select
    //     whatever polygon sits BEHIND it (e.g. Durham behind "Toronto").
    //     Users expect the label to select ITS city — hit-test label rects and
    //     forward the click to the matching dot ("<name>" or "<name> Metro
    //     Area"). Misses still fall through to the geometry beneath.
    var suppressClick = false;
    var forwarding = false; // true while we re-dispatch a click to a dot
    var lastGateClickTs = 0; // any click the gate processed (dedupe for tap synthesis)
    svg.addEventListener(
      "click",
      function (e) {
        if (forwarding) return; // our own forwarded clicks pass untouched
        lastClickTs = Date.now();
        lastGateClickTs = lastClickTs;
        if (suppressClick) {
          suppressClick = false;
          e.stopPropagation();
          e.preventDefault();
          return;
        }
        // pclbl = province-level city labels; natlbl = NATIONAL landmark labels
        // ("Toronto", "Ottawa-Gatineau"…) whose engine behaviour was only
        // "drill into the province" — users expect them to select THE CITY
        // (Tu bug 2026-07-14). natlbl clicks also fly the view to the city.
        // whole label ASSEMBLY is a hotspot: the ring/dot circles in g.pcity
        // have NO engine handler (only the separate small circle.dot is
        // wired), so clicking the big ring did nothing (Tu's Hamilton shot).
        // Forward any click on an assembly part to its city.
        var asm = e.target.closest && e.target.closest("g.pcity, g.natcity");
        if (asm) {
          var asmT = asm.querySelector("text");
          var asmNm = asmT ? (asmT.textContent || "").trim() : "";
          var asmDot = asmNm && (findDot(asmNm) || findDot(asmNm + " Metro Area") || findDotLoose(asmNm));
          if (asmDot) {
            e.stopPropagation();
            e.preventDefault();
            forwarding = true;
            asmDot.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));
            forwarding = false;
            if (asm.classList.contains("natcity")) {
              var ax = parseFloat(asmDot.getAttribute("cx"));
              var ay = parseFloat(asmDot.getAttribute("cy"));
              if (isFinite(ax) && isFinite(ay)) animateTo({ x: ax - 145, y: ay - 130.5, w: 290, h: 261 });
            }
            return;
          }
        }
        // labels can OVERLAP (Montréal / Ottawa-Gatineau stack in the
        // St. Lawrence corridor) — pick the hit whose center is NEAREST the
        // click, not the first in DOM order
        var labels = svg.querySelectorAll("text.pclbl, text.natlbl");
        var best = null;
        var bestD = Infinity;
        for (var i = 0; i < labels.length; i++) {
          var r = labels[i].getBoundingClientRect();
          if (r.width < 2) continue;
          if (e.clientX < r.left - 3 || e.clientX > r.right + 3 || e.clientY < r.top - 3 || e.clientY > r.bottom + 3) continue;
          var ddx = e.clientX - (r.left + r.width / 2);
          var ddy = e.clientY - (r.top + r.height / 2);
          var d = ddx * ddx + ddy * ddy;
          if (d < bestD) {
            bestD = d;
            best = labels[i];
          }
        }
        if (best) {
          var nm = (best.textContent || "").trim();
          var dot = findDot(nm) || findDot(nm + " Metro Area") || findDotLoose(nm);
          if (!dot) return; // no matching dot — let it fall through
          e.stopPropagation();
          e.preventDefault();
          forwarding = true;
          dot.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));
          forwarding = false;
          var isNat = ((best.getAttribute("class") || "").indexOf("natlbl") >= 0);
          if (isNat) {
            // fly to the city for context (engine's selectCity pins + renders
            // the panel but never moves the map; w=290 mirrors the engine's
            // own zoomToPoint scale)
            var dx = parseFloat(dot.getAttribute("cx"));
            var dy = parseFloat(dot.getAttribute("cy"));
            if (isFinite(dx) && isFinite(dy)) {
              animateTo({ x: dx - 145, y: dy - 130.5, w: 290, h: 261 });
            }
          }
          return;
        }
      },
      { capture: true }
    );
    function findDot(nm) {
      var dots = svg.querySelectorAll("circle.dot");
      for (var i = 0; i < dots.length; i++) {
        if ((dots[i].getAttribute("data-nm") || "") === nm) return dots[i];
      }
      return null;
    }
    function findDotLoose(nm) {
      // diacritic-insensitive: the landmark label says "Québec" but the dot's
      // data-nm is "Quebec"
      var want = fold(nm);
      var dots = svg.querySelectorAll("circle.dot");
      for (var i = 0; i < dots.length; i++) {
        var have = fold((dots[i].getAttribute("data-nm") || "").replace(" Metro Area", ""));
        if (have === want) return dots[i];
      }
      return null;
      function fold(s) {
        try {
          return s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
        } catch (err) {
          return s.toLowerCase();
        }
      }
    }

    // ── controls ──
    var box = document.createElement("div");
    box.className = "rt-mapzoom";
    var bIn = btn("+", FR ? "Zoom avant" : "Zoom in");
    var bOut = btn("−", FR ? "Zoom arrière" : "Zoom out");
    var bReset = btn("⤢", FR ? "Réinitialiser la vue" : "Reset view");
    bReset.className = "rt-mz-reset";
    box.appendChild(bIn);
    box.appendChild(bOut);
    box.appendChild(bReset);
    wrap.appendChild(box);

    function btn(txt, label) {
      var b = document.createElement("button");
      b.type = "button";
      b.textContent = txt;
      b.setAttribute("aria-label", label);
      b.title = label;
      return b;
    }
    function syncButtons() {
      bIn.disabled = view.w <= minW() + 0.5;
      bOut.disabled = view.w >= NATION.w - 0.5;
      bReset.disabled = !userModified();
    }
    function clientToVB(px, py) {
      var r = svg.getBoundingClientRect();
      var v = view;
      var s = Math.min(r.width / v.w, r.height / v.h);
      var ox = r.left + (r.width - v.w * s) / 2;
      var oy = r.top + (r.height - v.h * s) / 2;
      return { x: v.x + (px - ox) / s, y: v.y + (py - oy) / s };
    }
    function zoomTarget(px, py, factor) {
      var pt = clientToVB(px, py);
      var nw = Math.min(Math.max(view.w / factor, minW()), NATION.w);
      if (Math.abs(nw - view.w) < 0.05) return null;
      var k = nw / view.w;
      var cx = view.x + view.w / 2;
      var cy = view.y + view.h / 2;
      return {
        x: pt.x + (cx - pt.x) * k - nw / 2,
        y: pt.y + (cy - pt.y) * k - (nw * 0.9) / 2,
        w: nw,
        h: nw * 0.9,
      };
    }
    function zoomAt(px, py, factor) {
      var t = zoomTarget(px, py, factor);
      if (t) animateTo(t); // buttons / wheel / double-tap: eased
    }
    function zoomAtInstant(px, py, factor) {
      var t = zoomTarget(px, py, factor);
      if (t) setView(clampView(t)); // pinch: continuous, no tween
    }
    function center(el) {
      var r = svg.getBoundingClientRect();
      return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    }

    bIn.addEventListener("click", function () {
      var c = center();
      zoomAt(c.x, c.y, 1.6);
    });
    bOut.addEventListener("click", function () {
      var c = center();
      zoomAt(c.x, c.y, 1 / 1.6);
    });
    bReset.addEventListener("click", function () {
      animateTo({ x: engine.x, y: engine.y, w: engine.w, h: engine.h });
    });

    // ── gesture-hint overlay (Google-Maps-embed pattern, Tu 2026-07-15) ──
    // Plain wheel keeps scrolling the page but briefly shows a faded overlay
    // telling the user HOW to zoom; a single-finger drag on touch shows the
    // two-finger hint. pointer-events:none — never blocks interaction.
    var hint = document.createElement("div");
    hint.className = "rt-maphint";
    hint.setAttribute("aria-hidden", "true");
    wrap.appendChild(hint);
    var hintTimer = null;
    var IS_MAC = /Mac|iP(hone|ad|od)/.test(navigator.platform || "");
    var HINT_WHEEL = FR
      ? "Utilisez " + (IS_MAC ? "⌘" : "Ctrl") + " + molette pour zoomer sur la carte"
      : "Use " + (IS_MAC ? "⌘" : "Ctrl") + " + scroll to zoom the map";
    var HINT_TOUCH = FR
      ? "Utilisez deux doigts pour déplacer la carte"
      : "Use two fingers to move the map";
    function showHint(text) {
      hint.textContent = text;
      hint.classList.add("on");
      if (hintTimer) clearTimeout(hintTimer);
      hintTimer = setTimeout(function () {
        hint.classList.remove("on");
      }, 1200);
    }
    function hideHint() {
      if (hintTimer) clearTimeout(hintTimer);
      hint.classList.remove("on");
    }

    // ctrl/cmd + wheel zooms; plain wheel scrolls the page + shows the hint
    svg.addEventListener(
      "wheel",
      function (e) {
        if (!e.ctrlKey && !e.metaKey) {
          showHint(HINT_WHEEL);
          return;
        }
        hideHint();
        e.preventDefault();
        zoomAt(e.clientX, e.clientY, e.deltaY < 0 ? 1.3 : 1 / 1.3);
      },
      { passive: false }
    );

    svg.addEventListener("dblclick", function (e) {
      e.preventDefault();
      zoomAt(e.clientX, e.clientY, 1.6);
    });

    // ── touch & mouse movement (docs/RADONMAP.md §7) ──
    // Google-Maps-embed model on TOUCH: ONE finger never moves the map (the
    // page scrolls normally — the old single-finger pan fought scrolling and
    // its 5px threshold swallowed taps; Tu mobile report 2026-07-15); TWO
    // fingers pinch-zoom + pan the map. A non-passive touchmove blocker
    // preventDefaults only ≥2-finger gestures so the browser never page-zooms
    // over the map, while 1-finger scroll stays native. MOUSE keeps the
    // original drag-to-pan whenever the view is tighter than national.
    var drag = null; // mouse only
    var pointers = new Map(); // touch pointers on the svg
    var pinch = null;
    function dist(a, b) {
      var dx = a.x - b.x, dy = a.y - b.y;
      return Math.sqrt(dx * dx + dy * dy);
    }
    function midOf(a, b) {
      return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    }

    svg.addEventListener("pointerdown", function (e) {
      if (e.pointerType === "touch") {
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY });
        if (pointers.size === 1) {
          tap = { id: e.pointerId, target: e.target, x: e.clientX, y: e.clientY };
        } else {
          tap = null; // second finger = not a tap
        }
        if (pointers.size === 2) {
          var pts = Array.from(pointers.values());
          pinch = { d: dist(pts[0], pts[1]), mid: midOf(pts[0], pts[1]) };
          drag = null;
          hideHint(); // two fingers down = they got it
          if (anim) {
            cancelAnimationFrame(anim.raf);
            anim = null;
          }
          // capture ONLY for pinch — capturing a single touch retargets the
          // synthesized click to the svg, which bypasses the hotspot gate
          // (taps stopped selecting anything)
          pointers.forEach(function (_, id) {
            try { svg.setPointerCapture(id); } catch (err) { /* ok */ }
          });
        }
        return; // single touch: page scroll, taps → click gate
      }
      if (view.w >= NATION.w - 0.5 || !e.isPrimary || e.button !== 0) return;
      drag = { px: e.clientX, py: e.clientY, moved: false, id: e.pointerId };
    });

    svg.addEventListener("pointermove", function (e) {
      if (e.pointerType === "touch") {
        if (!pointers.has(e.pointerId)) return;
        var prev = pointers.get(e.pointerId);
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, sx: prev.sx, sy: prev.sy });
        if (!pinch && pointers.size === 1) {
          // single-finger drag = page scroll; teach the two-finger gesture —
          // but not right after a real pinch (they clearly know it)
          var mx = e.clientX - prev.sx;
          var my = e.clientY - prev.sy;
          if (mx * mx + my * my > 144 && Date.now() - lastPinchEnd > 1500) showHint(HINT_TOUCH);
          return;
        }
        if (!pinch || pointers.size < 2) return;
        var pts = Array.from(pointers.values());
        var d = dist(pts[0], pts[1]);
        var m = midOf(pts[0], pts[1]);
        if (pinch.d > 8 && d > 8) zoomAtInstant(m.x, m.y, d / pinch.d);
        var r = svg.getBoundingClientRect();
        var s = Math.min(r.width / view.w, r.height / view.h);
        if (s > 0) {
          setView(
            clampView({ x: view.x - (m.x - pinch.mid.x) / s, y: view.y - (m.y - pinch.mid.y) / s, w: view.w, h: view.h }),
            true // pan component — zoom already rescaled glyphs
          );
        }
        pinch.d = d;
        pinch.mid = m;
        return;
      }
      if (!drag || e.pointerId !== drag.id) return;
      var dx = e.clientX - drag.px;
      var dy = e.clientY - drag.py;
      if (!drag.moved && dx * dx + dy * dy < 25) return;
      if (!drag.moved) {
        drag.moved = true;
        try { svg.setPointerCapture(e.pointerId); } catch (err) { /* ok */ }
      }
      e.preventDefault();
      var r2 = svg.getBoundingClientRect();
      var s2 = Math.min(r2.width / view.w, r2.height / view.h);
      if (anim) {
        cancelAnimationFrame(anim.raf);
        anim = null;
      }
      setView(
        clampView({ x: view.x - dx / s2, y: view.y - dy / s2, w: view.w, h: view.h }),
        true // pan doesn't change zoom — glyphs unchanged
      );
      drag.px = e.clientX;
      drag.py = e.clientY;
    });

    var lastPinchEnd = 0;
    var tap = null; // candidate solo-tap gesture
    function endDrag(e) {
      if (e && e.pointerType === "touch") {
        pointers.delete(e.pointerId);
        if (pinch && pointers.size < 2) {
          pinch = null;
          tap = null;
          lastPinchEnd = Date.now();
          // a two-finger release can synthesize a click on some browsers
          suppressClick = true;
          setTimeout(function () {
            suppressClick = false;
          }, 250);
        }
        // TAP SYNTHESIS (Tu mobile r3): when a hover handler mutates the DOM
        // (the engine's mouseenter shows the tooltip), mobile browsers treat
        // the first tap as hover-intent and SUPPRESS its click — provinces
        // needed two taps ("shows the label first"). For a clean solo tap
        // (one finger, <10px movement), wait 150ms for the native click and
        // synthesize one on the original target if it never arrives. The
        // gate processes it like a real click (forwarding flag, not
        // isTrusted, distinguishes our dot re-dispatches).
        if (tap && e.pointerId === tap.id) {
          var t = tap;
          tap = null;
          var mvx = e.clientX - t.x;
          var mvy = e.clientY - t.y;
          if (mvx * mvx + mvy * mvy < 100 && !suppressClick) {
            setTimeout(function () {
              if (Date.now() - lastGateClickTs < 140) return; // native click arrived
              if (!t.target || !t.target.isConnected) return;
              t.target.dispatchEvent(
                new MouseEvent("click", { bubbles: true, cancelable: true, clientX: t.x, clientY: t.y })
              );
            }, 150);
          }
        }
        return;
      }
      if (!drag || (e && e.pointerId !== drag.id)) return;
      var moved = drag.moved;
      drag = null;
      if (moved) {
        // the click gate above consumes this flag and swallows the click
        suppressClick = true;
        setTimeout(function () {
          suppressClick = false;
        }, 150);
      }
    }
    // WINDOW-level, not svg-level: a finger that slides OFF the map before
    // lifting fires its pointerup outside the svg — an svg listener misses it
    // and leaves a GHOST in the tracker, so the next single finger reads as
    // finger #2 → phantom pinch + the hint branch never runs again (Tu,
    // 2026-07-15 r2). Window catches every release wherever it lands.
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);

    // browser must never page-zoom/scroll while TWO fingers are on the map;
    // one-finger gestures pass through untouched (native page scroll)
    svg.addEventListener(
      "touchmove",
      function (e) {
        if (e.touches.length >= 2) e.preventDefault();
      },
      { passive: false }
    );
    // iOS Safari's proprietary page-pinch gesture over the map
    svg.addEventListener("gesturestart", function (e) {
      e.preventDefault();
    });

    syncButtons();
    scaleGlyphs(); // apply the label cap to whatever the engine already rendered
  }
  // ── panel enhancement: scope-widened "already tested" pill ─────────────
  // The survey publishes city/metro figures as AGGREGATES — no sample counts
  // exist (Ger, 2026-07-14), so the engine's confNote() renders nothing for
  // them while CDs/HC-regions/provinces show "N households here have already
  // tested". Agreed compromise (Tu, 2026-07-14; docs/RADONMAP.md §7): when a
  // CITY/METRO panel renders without a count pill, inject one with a WIDER,
  // EXPLICITLY-NAMED scope — the province count for cities ("across
  // Saskatchewan", provData2024) or the national count for the 6 metros
  // ("across Canada", national.n = 69,478). Copy mirrors Ger's own approved
  // confNote sentence with only the scope phrase changed. If a future engine
  // drop renders its own pill (native fallback), the .conf guard makes this
  // injector a no-op.
  function bootPill() {
    var panel = document.getElementById("panel");
    var D = window.__MAPDATA__;
    if (!panel || !D) {
      if (PILL_TRIES++ < 20) setTimeout(bootPill, 250);
      return;
    }
    if (panel.__rtPillWired) return;
    panel.__rtPillWired = true;

    var FR = (document.documentElement.lang || "").toLowerCase().indexOf("fr") === 0;
    var PROV_EN = {
      nl: "Newfoundland and Labrador", ns: "Nova Scotia", nb: "New Brunswick",
      pe: "Prince Edward Island", qc: "Quebec", on: "Ontario", mb: "Manitoba",
      sk: "Saskatchewan", ab: "Alberta", bc: "British Columbia", yt: "Yukon",
    };
    // French needs the preposition baked in (en/au/à/à l')
    var PROV_FR = {
      nl: "à Terre-Neuve-et-Labrador", ns: "en Nouvelle-Écosse",
      nb: "au Nouveau-Brunswick", pe: "à l’Île-du-Prince-Édouard",
      qc: "au Québec", on: "en Ontario", mb: "au Manitoba",
      sk: "en Saskatchewan", ab: "en Alberta",
      bc: "en Colombie-Britannique", yt: "au Yukon",
    };

    function areaFor(nm) {
      var i;
      var metros = D.metros || [];
      for (i = 0; i < metros.length; i++) {
        if (metros[i].name === nm || metros[i].short === nm) return { kind: "metro" };
      }
      var cities = D.cities || [];
      for (i = 0; i < cities.length; i++) {
        if (cities[i].name === nm) return { kind: "city", prov: (cities[i].prov || "").toLowerCase() };
      }
      return null;
    }

    function fmt(n) {
      return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, FR ? " " : ",");
    }

    function inject() {
      if (panel.querySelector(".conf")) return; // engine (or we) already rendered one
      var nmEl = panel.querySelector(".nm");
      var kindEl = panel.querySelector(".kind");
      if (!nmEl || !kindEl) return;
      if ((kindEl.textContent || "").indexOf("2024") < 0) return; // 2024-survey panels only
      var area = areaFor((nmEl.textContent || "").trim());
      if (!area) return;

      var n = null;
      var scope = null;
      if (area.kind === "city" && area.prov && (D.provData2024 || {})[area.prov]) {
        n = D.provData2024[area.prov].n;
        scope = FR ? PROV_FR[area.prov] : "across " + PROV_EN[area.prov];
      }
      if (!n && D.national && D.national.n) {
        n = D.national.n;
        scope = FR ? "partout au Canada" : "across Canada";
      }
      if (!n || !scope) return;

      var body = FR
        ? "<b>foyers " + scope + " ont déjà testé</b> — joignez-vous à eux : le vôtre ne compte pas encore."
        : "<b>households " + scope + " have already tested</b> — join them: yours isn’t counted yet.";
      var pill = document.createElement("div");
      pill.className = "conf confctr rt-conf";
      pill.innerHTML =
        '<span class="cnum" data-n="' + n + '">' + fmt(n) + "</span>" +
        '<span class="conftxt"><span class="confdot" style="background:#1f7a4d"></span>' + body + "</span>";

      // Slot: INLINE directly before the More-detail accordion (or a buy CTA)
      // inside its own parent — the engine's own confNote position (under the
      // dark risk box). Never walk up to a top-level panel child: the whole
      // card is one wrapper, so inserting before it put the pill ABOVE the
      // panel header. City/metro buy is .cta2, not .cta.
      var target = panel.querySelector(".morea, .cta, .cta2");
      if (target) {
        target.parentNode.insertBefore(pill, target);
      } else {
        panel.appendChild(pill);
      }

      // The engine pairs counter → buy ("buy-mid", buyBelow()) but gates BOTH
      // on a.n — so city/metro panels were missing the buy button too (Tu,
      // 2026-07-14). Inject the engine's exact buymid block after the pill.
      // NOTE: D.brand.buy is the EN product URL in BOTH data files (flagged
      // to Ger) — use the /fr/ product URL on the FR page.
      if (!panel.querySelector(".buymid")) {
        var buy = document.createElement("div");
        buy.className = "buymid rt-buymid";
        var href = FR
          ? "https://radontest.ca/fr/pages/comparer-les-trousses-de-test-de-radon"
          : (D.brand && D.brand.buy) || "https://radontest.ca/pages/compare-radon-test-kits";
        buy.innerHTML =
          '<a class="cta2 cta2-mid" data-evt="buy-mid" href="' + href + '" target="_blank" rel="noopener">' +
          (FR ? "Achetez votre trousse de test de radon maintenant" : "Buy your Radon Test Kit Now") +
          '</a><div class="bc-priv">' +
          (FR
            ? "🔒 Votre test n’apparaît jamais ici sans votre accord — et alors seulement en statistiques anonymes de secteur, jamais votre nom ni votre adresse."
            : "🔒 Test with us and your result never appears here unless you opt in — and then only as anonymous area stats, never your name or address.") +
          "</div>";
        pill.parentNode.insertBefore(buy, pill.nextSibling);
      }
    }

    var pRaf = null;
    new MutationObserver(function () {
      if (pRaf) return;
      pRaf = requestAnimationFrame(function () {
        pRaf = null;
        inject();
      });
    }).observe(panel, { childList: true });
    inject();
  }
  var PILL_TRIES = 0;

  // ── border flushness: fill-coloured underlay beneath the region tiles ──
  // The CD/HC polygons are simplified independently, so neighbouring edges
  // don't share vertices — hairline-to-2-unit white CRACKS open between
  // tiles and the borders read uneven (Tu screenshot, 2026-07-14). For every
  // visible region path, draw a clone UNDER all originals, stroked ~2.2
  // viewBox units in its own fill colour: the expansion closes the geometry
  // gaps from below while the engine's uniform white strokes stay crisp on
  // top. Clones are pointer-events:none (hover/click untouched) and rebuilt
  // on engine re-renders and threshold recolours.
  function bootBorders() {
    var svg = document.getElementById("map");
    if (!svg) {
      if (BORDER_TRIES++ < 20) setTimeout(bootBorders, 250);
      return;
    }
    if (svg.__rtBordersWired) return;
    svg.__rtBordersWired = true;

    var UNDERLAY_STROKE = 2.2; // viewBox units — covers the observed cracks

    function rebuild() {
      var i;
      var old = svg.querySelectorAll("g.rt-underlay");
      for (i = 0; i < old.length; i++) old[i].parentNode.removeChild(old[i]);

      var paths = svg.querySelectorAll("path.cd, path.hc");
      var byParent = new Map();
      for (i = 0; i < paths.length; i++) {
        var p = paths[i];
        if (getComputedStyle(p).display === "none") continue;
        var fill = getComputedStyle(p).fill;
        if (!fill || fill === "none") continue;
        var clone = document.createElementNS("http://www.w3.org/2000/svg", "path");
        clone.setAttribute("d", p.getAttribute("d"));
        clone.setAttribute("fill", fill);
        clone.setAttribute("stroke", fill);
        clone.setAttribute("stroke-width", UNDERLAY_STROKE);
        clone.setAttribute("stroke-linejoin", "round");
        clone.setAttribute("pointer-events", "none");
        clone.setAttribute("aria-hidden", "true");
        var g = byParent.get(p.parentNode);
        if (!g) {
          g = document.createElementNS("http://www.w3.org/2000/svg", "g");
          g.setAttribute("class", "rt-underlay");
          byParent.set(p.parentNode, g);
        }
        g.appendChild(clone);
      }
      byParent.forEach(function (g, parent) {
        parent.insertBefore(g, parent.firstChild); // beneath every original
      });
    }

    function isOurs(node) {
      return (
        node &&
        node.nodeType === 1 &&
        ((node.classList && node.classList.contains("rt-underlay")) ||
          (node.closest && node.closest("g.rt-underlay")))
      );
    }
    function displayOf(styleText) {
      var m = /display\s*:\s*([^;]*)/.exec(styleText || "");
      return m ? m[1].trim() : "";
    }
    var bRaf = null;
    new MutationObserver(function (records) {
      var relevant = false;
      for (var i = 0; i < records.length; i++) {
        var r = records[i];
        var t = r.target;
        if (isOurs(t)) continue; // our own underlay mutations
        if (r.type === "childList") {
          // ignore batches that only add/remove underlay groups (our rebuild)
          var nodes = [].slice.call(r.addedNodes).concat([].slice.call(r.removedNodes));
          var onlyOurs = nodes.length > 0;
          for (var j = 0; j < nodes.length; j++) {
            if (!isOurs(nodes[j])) onlyOurs = false;
          }
          if (onlyOurs) continue;
          if (!nodes.length) continue;
        } else if (r.attributeName === "style") {
          // engine toggles region visibility via style.display on drill;
          // our zoom module writes style.strokeWidth every animation frame —
          // only a DISPLAY change matters here
          if (displayOf(r.oldValue) === displayOf(t.getAttribute("style"))) continue;
        }
        relevant = true;
        break;
      }
      if (!relevant || bRaf) return;
      bRaf = requestAnimationFrame(function () {
        bRaf = null;
        rebuild();
      });
    }).observe(svg, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["fill", "style"],
      attributeOldValue: true,
    });
    rebuild();
  }
  var BORDER_TRIES = 0;

  // ── "More info" link in the tap tooltip — NARROW LAYOUT ONLY (Tu r4) ────
  // Tapping a region shows the engine's #tip ("Durham 32.5% ≥100"). On the
  // SINGLE-COLUMN layout (≤760px, matching the CSS breakpoint) the panel is
  // BELOW THE FOLD, so we add a link that scrolls to it. The link is scoped
  // to that layout ON PURPOSE: on wide desktop the panel already sits beside
  // the map (no link needed), AND the engine's #tip is a HOVER tooltip that
  // repositions to each region's centroid — a link inside it flickers and is
  // unclickable there (Tu r4). Narrow layouts are touch (tap tooltip, no
  // hover, no flicker). The tip is pointer-events:none and rebuilt via
  // innerHTML on every show, so the link (pointer-events:auto via CSS) is
  // re-appended by a childList observer; the media query is re-checked each
  // time, so a resize across the breakpoint adds/removes it on the next tip.
  function bootTip() {
    var tip = document.getElementById("tip");
    if (!tip) {
      if (TIP_TRIES++ < 20) setTimeout(bootTip, 250);
      return;
    }
    if (tip.__rtMoreWired) return;
    tip.__rtMoreWired = true;

    var FR = (document.documentElement.lang || "").toLowerCase().indexOf("fr") === 0;
    var narrowMQ = window.matchMedia("(max-width: 760px)");

    function ensureLink() {
      if (!narrowMQ.matches) {
        var stale = tip.querySelector(".rt-tipmore");
        if (stale) stale.remove(); // wide layout — no link (panel is visible)
        return;
      }
      if (tip.querySelector(".rt-tipmore")) return;
      var b = document.createElement("button");
      b.type = "button";
      b.className = "rt-tipmore";
      b.textContent = FR ? "Plus d’infos ↓" : "More info ↓";
      var go = function (ev) {
        // pointerdown, NOT click: on touch, tapping the link fires the old
        // region's mouseleave (engine hideTip) BEFORE the click lands — the
        // button vanished under the finger and the click fell through to the
        // map (Tu mobile r3). pointerdown precedes the compatibility mouse
        // events; preventDefault stops the fall-through click entirely.
        ev.stopPropagation();
        ev.preventDefault();
        tip.style.display = "none";
        var p = document.getElementById("panel");
        if (p && p.scrollIntoView) p.scrollIntoView({ behavior: "smooth", block: "start" });
      };
      b.addEventListener("pointerdown", go);
      b.addEventListener("click", go); // keyboard / synthetic activation
      tip.appendChild(b);
    }
    new MutationObserver(ensureLink).observe(tip, { childList: true });
    if (narrowMQ.addEventListener) narrowMQ.addEventListener("change", ensureLink);
    ensureLink();
  }
  var TIP_TRIES = 0;

  // ── cross-provincial back-button → the ORIGIN province (Tu r5/r6) ────────
  // 3 CDs span two provinces (data: "Labrador & Québec North Shore" NL,QC;
  // "Prince Edward Island" PE,QC; "Northern Alberta & S. NWT" AB,NT). The
  // engine's onCDclick/selectCD set curProv = prov2(a.prov) = the FIRST
  // province, so drilling one from Québec sets curProv=nl → the "← Back to"
  // button BOTH labels and (via backOut→drill(curProv)) NAVIGATES to
  // Newfoundland & Labrador, not the Québec you came from. We fix BOTH: track
  // the origin province (the one being viewed) and, when a cross-provincial CD
  // is drilled, (1) relabel the button + crumb prefix to the origin and (2)
  // intercept the back click to drill the ORIGIN province path instead. Only
  // engages on a genuine mismatch (normal CDs untouched). Flagged to Ger to
  // fix at source (preserve the pre-drill curProv when drilling from a
  // province view).
  var PROVNM_EN = { on: "Ontario", qc: "Québec", bc: "British Columbia", ab: "Alberta", sk: "Saskatchewan", mb: "Manitoba", ns: "Nova Scotia", nb: "New Brunswick", nl: "Newfoundland & Labrador", pe: "Prince Edward Island", yt: "Yukon", nt: "Northwest Territories", nu: "Nunavut" };
  var PROVNM_FR = { on: "Ontario", qc: "Québec", bc: "Colombie-Britannique", ab: "Alberta", sk: "Saskatchewan", mb: "Manitoba", ns: "Nouvelle-Écosse", nb: "Nouveau-Brunswick", nl: "Terre-Neuve-et-Labrador", pe: "Île-du-Prince-Édouard", yt: "Yukon", nt: "Territoires du Nord-Ouest", nu: "Nunavut" };
  function bootCrumb() {
    var crumb = document.getElementById("crumbtxt");
    var zoom = document.getElementById("zoomout");
    var svg = document.getElementById("map");
    if (!crumb || !zoom || !svg) {
      if (CRUMB_TRIES++ < 20) setTimeout(bootCrumb, 250);
      return;
    }
    if (zoom.__rtCrumbWired) return;
    zoom.__rtCrumbWired = true;

    var FR = (document.documentElement.lang || "").toLowerCase().indexOf("fr") === 0;
    var PROVNM = FR ? PROVNM_FR : PROVNM_EN;
    var NAME2SLUG = {};
    Object.keys(PROVNM).forEach(function (s) { NAME2SLUG[PROVNM[s].toLowerCase()] = s; });
    var SEP = "›";
    var originSlug = null; // the province currently being viewed
    var correctSlug = null; // active back-button correction target, else null

    function slugOf(nm) { return NAME2SLUG[(nm || "").trim().toLowerCase()] || null; }
    function name(slug) { return PROVNM[slug] || slug; }

    // capture the origin slug directly from a province drill-in click
    svg.addEventListener("click", function (e) {
      var pf = e.target.closest && e.target.closest("path.provfill");
      if (pf && pf.getAttribute("data-prov")) originSlug = pf.getAttribute("data-prov");
    }, true);

    function onCrumb() {
      var t = (crumb.textContent || "").trim();
      var mv = t.match(/^Viewing\s+(.+)$/) || t.match(/^Affichage\s*:\s*(.+)$/);
      if (mv) { var s = slugOf(mv[1]); if (s) originSlug = s; correctSlug = null; return; }
      var sep = t.indexOf(SEP);
      if (sep <= 0) return;
      var prefixSlug = slugOf(t.slice(0, sep));
      if (originSlug && prefixSlug && prefixSlug !== originSlug) {
        correctSlug = originSlug;
        var want = name(originSlug) + " " + SEP + " " + t.slice(sep + 1).trim();
        if (t !== want) crumb.textContent = want;
      }
    }
    function onZoom() {
      var t = (zoom.textContent || "").trim();
      if (/All of Canada|Tout le Canada/.test(t)) { correctSlug = null; return; }
      var m = t.match(/(Back to|Retour à)\s+(.+)$/);
      if (!m) return;
      var backSlug = slugOf(m[2]);
      if (originSlug && backSlug && backSlug !== originSlug) {
        correctSlug = originSlug;
        var fixed = t.slice(0, m.index) + m[1] + " " + name(originSlug);
        if (t !== fixed) zoom.textContent = fixed;
      }
    }

    new MutationObserver(onZoom).observe(zoom, { childList: true, characterData: true, subtree: true });
    new MutationObserver(onCrumb).observe(crumb, { childList: true, characterData: true, subtree: true });
    new MutationObserver(function () {
      if (zoom.style.display === "none") { originSlug = null; correctSlug = null; }
    }).observe(zoom, { attributes: true, attributeFilter: ["style"] });

    // intercept the back-button click → drill the ORIGIN province, not curProv
    document.addEventListener("click", function (e) {
      if (!correctSlug || !(e.target.closest && e.target.closest("#zoomout"))) return;
      var pf = svg.querySelector('path.provfill[data-prov="' + correctSlug + '"]');
      if (!pf) return; // not found — let the engine handle it
      e.preventDefault();
      e.stopPropagation();
      var want = correctSlug;
      correctSlug = null;
      pf.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));
      originSlug = want;
    }, true);

    onZoom();
    onCrumb();
  }
  var CRUMB_TRIES = 0;

  // ── share row: swap broken Facebook + Instagram for Copy link + native Share ──
  // The engine's .shrow gives Text · Email · Facebook · Instagram. Two don't work:
  // Facebook's sharer.php now 302s to a login-gated flow (same reason the blog's
  // share-buttons.liquid dropped it), and "Instagram" has no web-share target —
  // on desktop it just downloads a PNG. Mirror the blog pattern instead: keep the
  // working Text + Email, and replace FB/IG with a Copy-link button (the reliable
  // desktop path, with copied-feedback) + a native Share button (mobile + capable
  // desktop only, feature-detected — its OS sheet already offers IG/FB/WhatsApp/…).
  // The URL (with the current area's deep-link param) + share text are lifted from
  // the engine's own buttons, so wording + locale stay verbatim. Re-runs on every
  // panel rebuild (threshold/area change) via the #panel childList observer.
  function bootShare() {
    var panel = document.getElementById("panel");
    if (!panel) {
      if (SHARE_TRIES++ < 20) setTimeout(bootShare, 250);
      return;
    }
    var FR = (document.documentElement.lang || "").toLowerCase().indexOf("fr") === 0;
    var L = FR
      ? {
          copy: "Copier le lien", copied: "Lien copié !", share: "Partager",
          dl: "⬇ Télécharger l’image",
          title: "📤 Partagez cette carte et votre secteur avec vos proches et d’autres propriétaires, courtiers, médias, inspecteurs",
        }
      : {
          copy: "Copy link", copied: "Link copied!", share: "Share",
          dl: "⬇ Download image",
          title: "📤 Share this map and your area with friends and other homeowners, realtors, media, inspectors",
        };

    function fallbackCopy(value, onDone) {
      try {
        var ta = document.createElement("textarea");
        ta.value = value;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        onDone();
      } catch (e) {
        /* swallow */
      }
    }

    function processRow(sb) {
      if (sb.getAttribute("data-rt-share")) return;
      sb.setAttribute("data-rt-share", "1");

      // Consolidate the old "Use or share this map" section into this panel
      // (Tu r18): retitle it, and (below) add a full-width Download-image
      // button. The engine rebuilds .shrow each panel update, so this
      // re-applies every time.
      var shrow = sb.closest(".shrow");
      if (shrow) {
        var lab = shrow.querySelector(".shlab");
        if (lab) lab.textContent = L.title;
      }

      var fb = sb.querySelector('[data-evt="share-fb"]');
      var email = sb.querySelector('[data-evt="share-email"]');
      var ig = sb.querySelector('[data-evt="share-ig"]');

      // URL (deep-linked to the current area) from FB's u= param; text from the
      // email body — both are the engine's own localized strings.
      var url = "";
      try {
        if (fb) url = new URL(fb.href).searchParams.get("u") || "";
      } catch (e) { /* ignore */ }
      if (!url) url = location.origin + "/pages/canada-radon-map";

      var text = "";
      if (email) {
        var m = /[?&]body=([^&]*)/.exec(email.getAttribute("href") || "");
        if (m) {
          try {
            text = decodeURIComponent(m[1].replace(/\+/g, " "));
          } catch (e2) {
            text = m[1];
          }
        }
      }
      // trim the trailing URL (and its "Check your area:" lead-in) out of the
      // native-share text so the link isn't duplicated with the url field.
      var shareText = text;
      if (url && text.indexOf(url) >= 0) {
        shareText = text.slice(0, text.indexOf(url)).replace(/\s*[:：]\s*$/, "").trim();
      }
      if (!shareText) shareText = FR ? "Carte du radon au Canada" : "Canada Radon Map";

      if (fb && fb.parentNode) fb.parentNode.removeChild(fb);
      if (ig && ig.parentNode) ig.parentNode.removeChild(ig);

      // Copy link (universal — desktop hero)
      var copy = document.createElement("button");
      copy.type = "button";
      copy.className = "shb rt-shcopy";
      copy.innerHTML = '🔗 <span class="rt-shcopy-lb">' + L.copy + "</span>";
      copy.addEventListener("click", function () {
        var lb = copy.querySelector(".rt-shcopy-lb");
        var done = function () {
          copy.setAttribute("data-copied", "1");
          if (lb) lb.textContent = L.copied;
          setTimeout(function () {
            copy.removeAttribute("data-copied");
            if (lb) lb.textContent = L.copy;
          }, 1800);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(done, function () {
            fallbackCopy(url, done);
          });
        } else {
          fallbackCopy(url, done);
        }
      });
      sb.appendChild(copy);

      // Native Share (mobile + capable desktop only — feature-detected)
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        var share = document.createElement("button");
        share.type = "button";
        share.className = "shb rt-shshare";
        share.innerHTML = "📤 <span>" + L.share + "</span>";
        share.addEventListener("click", function () {
          try {
            var p = navigator.share({ title: FR ? "Carte du radon au Canada" : "Canada Radon Map", text: shareText, url: url });
            if (p && p.catch) p.catch(function () {});
          } catch (e3) { /* ignore */ }
        });
        sb.appendChild(share);
      }

      // Download image — full-width row below (spans both grid columns via CSS).
      // Reuses the engine's own #downloadimg handler (wired by buildShare once
      // the #suform stub let the init chain finish, r14); that button lives in
      // the now-hidden "Use or share this map" fold — hidden, NOT removed, so it
      // stays in the DOM and .click() still fires (R44 asserts this coupling).
      var dl = document.createElement("button");
      dl.type = "button";
      dl.className = "shb rt-shdl";
      dl.textContent = L.dl;
      dl.addEventListener("click", function () {
        var d = document.getElementById("downloadimg");
        if (d) d.click();
      });
      sb.appendChild(dl);
    }

    function transform() {
      var rows = panel.querySelectorAll(".shrow .shbtns");
      for (var i = 0; i < rows.length; i++) processRow(rows[i]);
    }

    var sRaf = null;
    new MutationObserver(function () {
      if (sRaf) return;
      sRaf = requestAnimationFrame(function () {
        sRaf = null;
        transform();
      });
    }).observe(panel, { childList: true });
    transform();
  }
  var SHARE_TRIES = 0;

  // ── relocate the area link-lists below the map on desktop (Tu r15) ──────────
  // Drilled panels carry .blogs link lists ("In <Province>", "Radon basics …")
  // that make the right column much taller than the map, leaving a tall empty
  // band under the map card. On desktop move them into a left-column grid cell
  // BELOW the map (panelwrap spans both rows via CSS) so the whitespace is
  // shared. National panels have no .blogs (nothing to move). Mobile keeps them
  // in #panel — no change. Re-runs on every panel rebuild; reverts on resize.
  function bootBlogs() {
    var layout = document.querySelector(".layout");
    var panel = document.getElementById("panel");
    if (!layout || !panel) {
      if (BLOG_TRIES++ < 20) setTimeout(bootBlogs, 250);
      return;
    }
    var MQ = window.matchMedia("(min-width: 861px)");

    function place() {
      if (MQ.matches) {
        var fresh = Array.prototype.slice.call(panel.querySelectorAll(".blogs"));
        var col = layout.querySelector(".rt-blogscol");
        if (fresh.length) {
          if (!col) {
            col = document.createElement("div");
            col.className = "rt-blogscol";
            layout.appendChild(col);
          }
          while (col.firstChild) col.removeChild(col.firstChild);
          fresh.forEach(function (b) { col.appendChild(b); });
        } else if (col) {
          col.parentNode.removeChild(col);
        }
      } else {
        // mobile: put any relocated lists back at the end of #panel, drop the col
        var col2 = layout.querySelector(".rt-blogscol");
        if (col2) {
          Array.prototype.slice.call(col2.querySelectorAll(".blogs")).forEach(function (b) {
            panel.appendChild(b);
          });
          col2.parentNode.removeChild(col2);
        }
      }
    }

    var raf = null;
    new MutationObserver(function (records) {
      // only react to engine rebuilds (added nodes), not our own .blogs moves
      var rebuilt = records.some(function (r) { return r.addedNodes && r.addedNodes.length; });
      if (!rebuilt || raf) return;
      raf = requestAnimationFrame(function () { raf = null; place(); });
    }).observe(panel, { childList: true });
    if (MQ.addEventListener) MQ.addEventListener("change", place);
    else if (MQ.addListener) MQ.addListener(place);
    place();
  }
  var BLOG_TRIES = 0;

  // ── drop the iframe-embed reference in the .audience blurb (Tu r16) ──────────
  // The embed UI (.embedwrap) is hidden via CSS because Shopify blocks framing
  // (X-Frame-Options: DENY store-wide). The .audience line still says "…embed
  // the map above, or link to your region" — scrub the dead embed clause so only
  // "link to your region" remains (EN + FR). Static node, wire once.
  function bootEmbedDrop() {
    var aud = document.querySelector(".rtm .audience") || document.querySelector(".audience");
    if (!aud) {
      if (EMBED_TRIES++ < 20) setTimeout(bootEmbedDrop, 250);
      return;
    }
    if (aud.getAttribute("data-rt-embeddrop")) return;
    aud.setAttribute("data-rt-embeddrop", "1");
    // Ger audit P0-1: REMOVE the dead <iframe> embed box entirely (not just
    // CSS-hide it). Shopify blocks cross-origin framing, so the copy-embed
    // snippet renders a broken frame everywhere it's pasted. The share panel
    // already offers the two working options Ger wants — Copy link + Download
    // image. A real embed returns in Phase 4 (map.radontest.ca subdomain).
    var wrap = document.querySelector(".rtm .embedwrap") || document.querySelector(".embedwrap");
    if (wrap) wrap.remove();
    aud.innerHTML = aud.innerHTML
      .replace(/embed the map above,?\s*or\s*/i, "")
      .replace(/int[ée]grez la carte ci-dessus\s*ou\s*/i, "");
  }
  var EMBED_TRIES = 0;

  // ── ?threshold= param + who|epa|hc aliases (Ger audit P0-2) ───────────────────
  // The engine default is set to Canada 200 at the engine level (flash-free — see
  // radon-map-v9-{en,fr}.js `var thr="200"`). This adds Ger's requested
  // ?threshold=100|148|200 param with who|epa|hc aliases so campaign links can
  // open a specific view; it sets the initial state only. The engine's own
  // ?thr=100|148|200 keeps working (different param name, no conflict).
  // ── clamp the hover tooltip inside the clipped frame (Ger T1, item 4) ────────
  // T1 sets .mapwrap{overflow:hidden} so drilled regions/overlays can't spill
  // outside the frame or slide under the sticky header. But the engine positions
  // .tip from the hovered region's centroid, which pushes it past the frame edge
  // for edge regions — measured: BC's tooltip overflows 31px to the LEFT, so
  // clipping alone would cut the label. Ger's spec says clamp overlays inside the
  // box rather than cut them, so we pin .tip back within the frame after the
  // engine positions it. The `busy` guard + the 0.5px threshold stop the
  // style-mutation observer from re-triggering on its own write.
  function bootTipClamp() {
    var wrap = document.querySelector(".rtm .mapwrap") || document.querySelector(".mapwrap");
    var tip = wrap && wrap.querySelector(".tip");
    if (!wrap || !tip) {
      if (TIPC_TRIES++ < 20) setTimeout(bootTipClamp, 250);
      return;
    }
    var busy = false;
    function clamp() {
      if (busy) return;
      var tb = tip.getBoundingClientRect(), wb = wrap.getBoundingClientRect();
      if (!tb.width || !tb.height || !wb.width) return; // hidden → nothing to clamp
      var sl = parseFloat(tip.style.left), st = parseFloat(tip.style.top);
      if (isNaN(sl) && isNaN(st)) return;
      // The engine anchors .tip at the region centroid and then CENTRES it with a
      // CSS transform (measured: translate(-width/2, -height…)). So style.left is
      // NOT the visual left edge. Derive the transform's pixel offset from the
      // rendered rect and clamp the VISUAL box — clamping the raw anchor would be
      // off by half the tooltip width.
      var offX = tb.left - (wb.left + (isNaN(sl) ? 0 : sl));
      var offY = tb.top - (wb.top + (isNaN(st) ? 0 : st));
      var minL = 4 - offX, maxL = wb.width - tb.width - 4 - offX;
      var minT = 4 - offY, maxT = wb.height - tb.height - 4 - offY;
      var nl = isNaN(sl) ? sl : Math.max(minL, Math.min(sl, Math.max(minL, maxL)));
      var nt = isNaN(st) ? st : Math.max(minT, Math.min(st, Math.max(minT, maxT)));
      if ((!isNaN(sl) && Math.abs(nl - sl) > 0.5) || (!isNaN(st) && Math.abs(nt - st) > 0.5)) {
        busy = true;
        if (!isNaN(nl)) tip.style.left = nl + "px";
        if (!isNaN(nt)) tip.style.top = nt + "px";
        busy = false;
      }
    }
    new MutationObserver(clamp).observe(tip, { attributes: true, attributeFilter: ["style"] });
    clamp();
  }
  var TIPC_TRIES = 0;

  function bootThreshold() {
    var qs;
    try { qs = new URLSearchParams(location.search); } catch (e) { return; }
    var raw = (qs.get("threshold") || "").toLowerCase().trim();
    if (!raw) return;
    var MAP = { who: "100", epa: "148", hc: "200", "100": "100", "148": "148", "200": "200" };
    var id = MAP[raw];
    if (!id) return;
    var TRIES = 0;
    (function apply() {
      if (window.__setThr) { window.__setThr(id); return; }
      if (TRIES++ < 20) setTimeout(apply, 150);
    })();
  }

  // ── dir-table sample-size header: "n" → "Homes"/"Maisons" (Tu r20) ─────────
  // The engine's paintDir() labels the sample-size column with a bare lowercase
  // "n" — meaningless to homeowners. Rename it after every repaint (typing in
  // the filter, sorting, and threshold changes all rebuild the whole table).
  // data-k="n" is the engine's own sort key, so sorting is untouched; the
  // textContent guard keeps the observer from looping on its own rename.
  function bootDirHeader() {
    var wrap = document.querySelector(".dirwrap");
    if (!wrap) {
      if (DIRH_TRIES++ < 20) setTimeout(bootDirHeader, 250);
      return;
    }
    var FR = (document.documentElement.lang || "").toLowerCase().indexOf("fr") === 0;
    var LABEL = FR ? "Maisons" : "Homes";
    function rename() {
      var th = wrap.querySelector('th[data-k="n"]');
      if (th && th.textContent !== LABEL) th.textContent = LABEL;
    }
    var raf = null;
    new MutationObserver(function () {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = null; rename(); });
    }).observe(wrap, { childList: true, subtree: true });
    rename();
  }
  var DIRH_TRIES = 0;

  function start() {
    boot();
    bootPill();
    bootBorders();
    bootTip();
    bootCrumb();
    bootShare();
    bootBlogs();
    bootEmbedDrop();
    bootDirHeader();
    bootThreshold();
    bootTipClamp();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
